import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import FloatingContactIcons from '../../components/feature/FloatingContactIcons';
import { useContentManager } from '../../hooks/useContentManager';

export default function ThankYouPage() {
  const { getContentItem } = useContentManager();

  useEffect(() => {
    document.title = 'Thank You | Golden Tower';
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  // Fetch dynamic Golden Tower assets if available, fallback to high-res images
  const brandHeroText = getContentItem('brand', 'hero-text');
  const brandAbout = getContentItem('brand', 'about');
  
  const logoUrl =
    (brandHeroText?.metadata?.logo_url as string) ||
    'https://cdn.berkongroup.com/media/images/png%20logo%20golden_1765904823509.webp';

  const heroImage =
    (Array.isArray(brandAbout?.metadata?.images) && brandAbout?.metadata?.images[0]) ||
    'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=2000&q=85';

  return (
    <div className="min-h-screen bg-black text-white flex flex-col justify-between selection:bg-[#C5A46D] selection:text-black">
      <Header />

      <main className="flex-1">
        {/* ── 1. HERO: Golden Tower Image ─────────────────────────── */}
        <section className="relative h-[45vh] sm:h-[50vh] md:h-[58vh] w-full overflow-hidden bg-black">
          <img
            src={heroImage}
            alt="Golden Tower Luxury Residence"
            className="w-full h-full object-cover object-center filter brightness-[0.78]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/30 to-black"></div>

          {/* Centered Golden Tower Logo */}
          <div className="absolute inset-0 flex items-center justify-center p-4">
            <div className="transform transition-transform duration-700 hover:scale-105">
              <img
                src={logoUrl}
                alt="Golden Tower Logo"
                className="h-24 sm:h-32 md:h-40 lg:h-44 w-auto max-w-[320px] sm:max-w-[420px] object-contain filter drop-shadow-[0_4px_30px_rgba(0,0,0,0.95)]"
              />
            </div>
          </div>
        </section>

        {/* ── 2. CONTENT UNDER HERO ────────────────────────────────── */}
        <section className="relative bg-black text-white py-14 sm:py-18 md:py-24 px-6 text-center">
          <div className="max-w-2xl mx-auto space-y-6">
            {/* Subtle verification badge */}
            <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-[#C5A46D]/10 border border-[#C5A46D]/30 mb-2">
              <i className="ri-check-line text-2xl sm:text-3xl text-[#C5A46D]"></i>
            </div>

            {/* Heading under hero: Thank You! */}
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-light tracking-wide text-white leading-tight">
              Thank You!
            </h1>

            {/* Text: Our Sales team will connect with you shortly to discuss your interest. */}
            <p className="text-base sm:text-lg md:text-xl text-gray-300 font-light leading-relaxed max-w-xl mx-auto">
              Our Sales team will connect with you shortly to discuss your interest.
            </p>

            {/* Button: Homepage */}
            <div className="pt-6 sm:pt-8">
              <Link
                to="/"
                className="inline-block px-10 py-4 bg-[#C5A46D] hover:bg-[#b08f58] text-black font-semibold text-xs sm:text-sm tracking-[0.2em] uppercase transition-all duration-300 shadow-lg hover:shadow-[#C5A46D]/20 cursor-pointer"
              >
                Homepage
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingContactIcons page="thank-you" />
    </div>
  );
}
